import React from "react";
import { PlusCircle, Search, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { STATUSES, STATUS_LABELS, ORDER_TAB_FILTER_OPTIONS, SIMPLE_STATUS_LABELS } from "@/pages/admin/orders/orderConstants";

// Search bar + customer/salesman/payment/invoice/date filters + status tabs — shared by
// both the Orders tab (mode="orders": simplified Pending/In Progress/Ready status tabs, no
// payment/invoice-status filters since nothing here has been invoiced yet) and the Invoices
// tab (mode="invoices": full filter set, no "New Order" button).
export const OrdersFiltersBar = ({
  mode = "invoices",
  search, setSearch, onSearchSubmit, onNewOrder,
  shopFilter, setShopFilter, shops, salesmanFilter, setSalesmanFilter, salesmen,
  paymentFilter, setPaymentFilter, invoiceStatusFilter, setInvoiceStatusFilter,
  dateFrom, setDateFrom, dateTo, setDateTo, overdueOnly, setOverdueOnly,
  statusFilter, setStatusFilter,
}) => {
  const isOrders = mode === "orders";
  const statusOptions = isOrders ? ORDER_TAB_FILTER_OPTIONS : STATUSES;
  const statusLabels = isOrders ? SIMPLE_STATUS_LABELS : STATUS_LABELS;
  return (
  <>
    <form onSubmit={onSearchSubmit} className="flex items-center gap-2 mb-3">
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input data-testid="orders-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={isOrders ? "Search order no, customer, mobile" : "Search invoice no, order no, customer, mobile, GSTIN"} className="pl-8" />
      </div>
      <Button type="submit" size="sm" variant="secondary">Search</Button>
      {isOrders && (
        <Button data-testid="new-order-button" type="button" size="sm" className="gap-1.5 ml-auto" onClick={onNewOrder}>
          <PlusCircle className="h-4 w-4" /> New Order
        </Button>
      )}
    </form>

    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <Select value={shopFilter} onValueChange={setShopFilter}>
        <SelectTrigger data-testid="filter-shop" className="w-40"><SelectValue placeholder="Customer" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Customers</SelectItem>{shops.map((s) => <SelectItem key={s.id} value={s.id}>{s.shop_name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={salesmanFilter} onValueChange={setSalesmanFilter}>
        <SelectTrigger data-testid="filter-salesman" className="w-36"><SelectValue placeholder="Salesman" /></SelectTrigger>
        <SelectContent><SelectItem value="all">All Salesmen</SelectItem>{salesmen.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      {!isOrders && (
        <>
          <Select value={paymentFilter} onValueChange={setPaymentFilter}>
            <SelectTrigger data-testid="filter-payment-status" className="w-36"><SelectValue placeholder="Payment Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Payments</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="paid">Paid</SelectItem></SelectContent>
          </Select>
          <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
            <SelectTrigger data-testid="filter-invoice-status" className="w-36"><SelectValue placeholder="Invoice Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Invoices</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="issued">Issued</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
          </Select>
        </>
      )}
      <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
      <span className="text-xs text-muted-foreground">to</span>
      <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
      {!isOrders && (
        <Button data-testid="filter-overdue-toggle" type="button" size="sm" variant={overdueOnly ? "default" : "outline"} className="gap-1.5" onClick={() => setOverdueOnly((v) => !v)}>
          <AlertTriangle className="h-3.5 w-3.5" /> Overdue only
        </Button>
      )}
    </div>

    <Tabs value={statusFilter} onValueChange={setStatusFilter}>
      <TabsList className="flex-wrap h-auto mb-3">
        <TabsTrigger data-testid="orders-tab-all" value="all">All</TabsTrigger>
        {statusOptions.map((s) => <TabsTrigger data-testid={`orders-tab-${s}`} key={s} value={s}>{statusLabels[s]}</TabsTrigger>)}
      </TabsList>
    </Tabs>
  </>
  );
};
